import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Input, Modal } from '@/components/ui';
import { notifyCompanyEmployments } from '@/api/services/employment.service';
import type { HrPersonRef } from '@/api/services/hrWorkspace.service';
import { getErrorMessage } from '@/lib/getErrorMessage';

type Result = { name: string; status: 'sent' | 'failed'; reason?: string };

/** Sends one in-app message to the selected people, company by company. */
export function MessagePeopleDialog({
  people,
  onClose,
}: {
  people: Array<HrPersonRef & { name: string }>;
  onClose: () => void;
}): React.ReactElement {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [requestKey] = useState(() => crypto.randomUUID());
  const [results, setResults] = useState<Result[] | null>(null);

  const send = useMutation({
    mutationFn: async (): Promise<Result[]> => {
      const byCompany = new Map<string, string[]>();
      for (const person of people) {
        byCompany.set(person.companyId, [...(byCompany.get(person.companyId) ?? []), person.userId]);
      }
      // The key is scoped by company on the server, so one key covers a retry of all of them.
      const batches = await Promise.all(
        [...byCompany].map(([companyId, userIds]) =>
          notifyCompanyEmployments(companyId, {
            userIds,
            idempotencyKey: requestKey,
            title: title.trim(),
            message: message.trim(),
          })
        )
      );
      return batches.flatMap((batch) =>
        batch.results.map((result) => ({
          name: people.find((person) => person.userId === result.userId)?.name ?? result.userId,
          status: result.status,
          reason: result.reason,
        }))
      );
    },
    onSuccess: (next) => {
      const sent = next.filter((result) => result.status === 'sent').length;
      const failed = next.length - sent;
      toast.success(
        failed > 0
          ? `Message sent to ${sent}; ${failed} could not be delivered`
          : `Message sent to ${sent} ${sent === 1 ? 'person' : 'people'}`
      );
      setResults(next);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  return (
    <Modal
      open
      onClose={() => !send.isPending && onClose()}
      title={`Message ${people.length} ${people.length === 1 ? 'person' : 'people'}`}
      footer={
        results ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" disabled={send.isPending} onClick={onClose}>
              Cancel
            </Button>
            <Button
              loading={send.isPending}
              disabled={!title.trim() || !message.trim()}
              onClick={() => send.mutate()}
            >
              Send message
            </Button>
          </div>
        )
      }
    >
      {results ? (
        <ul
          aria-label="Message delivery results"
          className="max-h-80 divide-y divide-gray-200 overflow-y-auto rounded-xl border border-gray-200 dark:divide-gray-700 dark:border-gray-700"
        >
          {results.map((result, index) => (
            <li key={`${result.name}-${index}`} className="flex items-start justify-between gap-4 px-4 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                  {result.name}
                </p>
                {result.reason && (
                  <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">{result.reason}</p>
                )}
              </div>
              <span
                className={`shrink-0 text-xs font-medium ${
                  result.status === 'sent'
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : 'text-red-700 dark:text-red-300'
                }`}
              >
                {result.status === 'sent' ? 'Sent' : 'Failed'}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="space-y-4">
          <Input
            id="bulk-message-title"
            label="Title"
            value={title}
            maxLength={80}
            required
            onChange={(event) => setTitle(event.target.value)}
          />
          <div>
            <label
              htmlFor="bulk-message-body"
              className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400"
            >
              Message
            </label>
            <textarea
              id="bulk-message-body"
              value={message}
              maxLength={500}
              required
              rows={5}
              onChange={(event) => setMessage(event.target.value)}
              className="w-full resize-y rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-sm text-gray-900 shadow-sm outline-none transition focus:border-primary/40 focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            />
            <p className="mt-1 text-right text-xs tabular-nums text-gray-500 dark:text-gray-400">
              {message.length}/500
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
}
